import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { useLocale } from '@/composables/inertia/shared-props';
import { useEUIsoCodes } from '@/hooks/useCountries';
import { getInitData } from '@/actions/wizard/RequestWizardActions';
import axios from 'axios';

// Type definitions
interface City {
    id: number;
    name: string;
}

interface InputOption {
    label: string;
    value: number;
}

interface ValidationResponse {
    is_valid: boolean;
    is_international: boolean;
}

interface ValidationChangeParams {
    isValidLocation: boolean;
    isInternational: boolean;
    error: boolean | string;
}

interface UseLocationAutoCompleteParams {
    initialPostcode?: string;
    initialCountry?: string;
    onValidationChange?: (params: ValidationChangeParams) => void;
    onLocationChange?: (location: string, country: string) => void;
}

interface RequestWizardState {
    requestWizard: {
        options: {
            cities: Record<string, City[]>;
        };
    };
}

export default function useLocationAutoComplete({
    initialPostcode = '',
    initialCountry = '',
    onValidationChange = () => {},
    onLocationChange = () => {},
}: UseLocationAutoCompleteParams) {
    const [postcode, setPostcode] = useState<string>(initialPostcode);
    const [country, setCountry] = useState<string>(initialCountry);
    const [filteredCities, setFilteredCities] = useState<InputOption[]>([]);
    const [postcodes, setPostcodes] = useState<string[]>([]);
    const [page, setPage] = useState<number>(1);
    const [isValidLocation, setIsValidLocation] = useState<boolean>(false);
    const [isInternational, setIsInternational] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<boolean | string>(
        false,
    );
    const dispatch = useDispatch();
    const { options, status } = useSelector(
        (state: RequestWizardState) => state.requestWizard,
    );
    const locale = useLocale();
    const euIsoCodes = useEUIsoCodes();
    const cities = options?.cities || {};

    // Use ref to store the callback function to avoid dependency issues
    const onValidationChangeRef = useRef(onValidationChange);
    onValidationChangeRef.current = onValidationChange;

    const loadMore = (): void => setPage(page + 1);
    const resetPage = (): void => setPage(1);

    const priorityCities: number[] = [
        73, // Brighton
        7, // Wales
        119, // Sussex
        66, // Cotswold
        83, // Bath
        129, // Edinburgh
        3, // Manchester
        132, // Cornwall
    ];

    // If cities aren't already loaded by the wizard, fetch. (likely chefs side)
    useEffect(() => {
        setTimeout(() => {
            if (!status.init) {
                dispatch(getInitData());
            }
        }, 3000);

        // Default country to the user's IP location, unless explicitly set
        if (!initialCountry || initialCountry === '') {
            setCountry(locale.country);
        }
    }, []);

    const toComparable = (v: string): string =>
        v?.toLowerCase()?.replace(/\s/g, '') ?? v;

    const getMatchedLocation = (v?: string): string | undefined =>
        cities?.[country]?.find(
            (city: City) =>
                toComparable(city.name) === toComparable(v ?? postcode),
        )?.name ??
        postcodes?.find(
            (postcode: string) =>
                toComparable(postcode) === toComparable(v ?? postcode),
        );

    const postcodeAutoComplete = useCallback(
        debounce(async (postcode: string): Promise<void> => {
            if (postcode.length < 2) return;

            try {
                let suggestions: string[] = [];

                if (country === 'GB') {
                    // postcode.io autocomplete is UK-only
                    const url = route('postcode.autocomplete', { postcode });
                    const response = await axios.get<string[]>(url);
                    suggestions = response.data || [];
                } else if (country !== 'US') {
                    // Google Places autocomplete for EU
                    const url = route('places.autocomplete', { query: postcode });
                    const response = await axios.get<string[]>(url, {
                        params: { country },
                    });
                    suggestions = response.data || [];
                }

                setPostcodes(suggestions);
            } catch (error) {
                console.error('Autocomplete error:', error);
                setPostcodes([]);
            }
        }, 300),
        [country],
    );

    const validate = useCallback(
        debounce(
            async (postcode: string): Promise<ValidationResponse | false> => {
                const url = route('postcode.validate', { postcode });
                try {
                    const response = await axios.get<ValidationResponse>(url, {
                        params: {
                            country: country,
                        },
                    });

                    const locationTerm = country === 'US' ? 'zip code' : country === 'GB' ? 'postcode' : 'postal code';
                    const errorMessage = response.data.is_valid
                        ? false
                        : `Please enter a valid city or ${locationTerm}.`;

                    // Only call onValidationChange if the validation state has actually changed
                    if (
                        isValidLocation !== response.data.is_valid ||
                        isInternational !== response.data.is_international
                    ) {
                        onValidationChangeRef.current({
                            isValidLocation: response.data.is_valid,
                            isInternational: response.data.is_international,
                            error: errorMessage,
                        });
                    }

                    setIsInternational(response.data.is_international);
                    setIsValidLocation(response.data.is_valid);
                    setValidationError(errorMessage);

                    return response.data;
                } catch (error) {
                    console.error('Validation error:', error);
                    setIsValidLocation(false);
                    setIsInternational(false);
                    setValidationError(false);
                    onValidationChangeRef.current({
                        isValidLocation: false,
                        isInternational: false,
                        error: false,
                    });
                    return false;
                }
            },
            500,
        ),
        [country],
    );

    // Filter cities based on postcode input
    useEffect(() => {
        const filtered = cities?.[country]
            ?.filter((city: City) => {
                if (postcode === '' || !postcode) return false;
                return toComparable(city.name)?.includes(
                    toComparable(postcode),
                );
            })
            ?.map(
                (city: City): InputOption => ({
                    label: city.name,
                    value: city.id,
                }),
            )
            ?.sort(
                (a: InputOption, b: InputOption) =>
                    priorityCities.indexOf(b.value) -
                    priorityCities.indexOf(a.value),
            )
            ?.slice(0, 10 * page);

        setFilteredCities(filtered || []);
    }, [postcode, cities, page]);

    // Handle location validation
    useEffect(() => {
        validate.cancel();
        const matchedLocation = getMatchedLocation(postcode);

        if (matchedLocation) {
            // Only call onValidationChange if the validation state has actually changed
            if (!isValidLocation || isInternational) {
                onValidationChangeRef.current({
                    isValidLocation: true,
                    isInternational: false,
                    error: false,
                });
            }
            // setPostcode(matchedLocation);
            setIsValidLocation(true);
            setIsInternational(false);
            setValidationError(false);
            onLocationChange(matchedLocation, country);
            return;
        }

        // Location isn't recognized as a city or postcode
        if (
            postcode?.length > 2 &&
            filteredCities?.length + postcodes?.length === 0
        ) {
            validate(postcode);
            return;
        }

        // Location isn't valid but there are suggestions available (still typing)
        if (isValidLocation || isInternational) {
            // Only call onValidationChange if the validation state has actually changed
            onValidationChangeRef.current({
                isValidLocation: false,
                isInternational: false,
                error: false,
            });
        }
        setIsValidLocation(false);
        setIsInternational(false);
        setValidationError(false);
    }, [postcode, postcodes, filteredCities]);

    // Handle country change
    const handleCountryChange = (newCountry: string): void => {
        if (newCountry === country) return;
        setCountry(newCountry);
        setPostcode('');
        setIsValidLocation(false);
        setValidationError(false);
        onLocationChange('', newCountry);
        onValidationChangeRef.current({
            isValidLocation: false,
            isInternational: false,
            error: false,
        });
    };

    // Handle postcode change
    const handlePostcodeChange = (newPostcode: string): void => {
        resetPage();
        setPostcode(newPostcode);
        onLocationChange(newPostcode, country);

        if (
            newPostcode.length < 3 ||
            // if no integers, it's not a postcode
            (country === 'GB' && !/\d/.test(newPostcode))
        ) {
            setPostcodes([]);
            return;
        }
        postcodeAutoComplete(newPostcode);
    };

    return {
        // State
        postcode,
        country,
        filteredCities,
        postcodes,
        isValidLocation,
        isInternational,
        validationError,

        // Actions
        handlePostcodeChange,
        handleCountryChange,
        loadMore,
        resetPage,
        toComparable,
        getMatchedLocation,

        // Computed
        suggestions:
            country === 'US'
                ? filteredCities
                : [
                      ...(postcodes?.map((p: string) => ({
                          label: p,
                          value: toComparable(p),
                      })) || []),
                      ...(filteredCities || []),
                  ],
    };
}
